import { supabase } from './supabase';

export async function seedJobsNow() {
  try {
    console.log('Starting job seeding...');

    const { data: sources, error: sourcesError } = await supabase
      .from('job_sources')
      .select('id, name');

    if (sourcesError) {
      console.error('Error fetching job sources:', sourcesError);
      return { success: false, error: sourcesError };
    }

    if (!sources || sources.length === 0) {
      console.error('No job sources found in database');
      return { success: false, error: 'No job sources found' };
    }

    const sourceMap = new Map(sources.map(s => [s.name, s.id]));
    console.log('Found sources:', Array.from(sourceMap.keys()));

    const testJobs = [
      {
        source_name: 'Indeed',
        external_id: 'IND-001',
        title: 'Senior Software Engineer',
        company: 'ResuSwipe Tech Solutions',
        location: 'Detroit, MI',
        remote_type: 'hybrid' as const,
        salary_min: 120000,
        salary_max: 160000,
        employment_type: 'full_time' as const,
        description: 'Join our dynamic team building next-generation job matching platforms. Work with cutting-edge technologies and make an impact on millions of job seekers.',
        requirements: 'Bachelor\'s degree in Computer Science. 5+ years experience with React, Node.js, and PostgreSQL. Strong problem-solving skills.',
        url: 'https://example.com/jobs/ind-001',
        skills_required: ['React', 'Node.js', 'PostgreSQL', 'TypeScript', 'AWS'],
        experience_level: 'senior' as const,
        industry: 'technology'
      },
      {
        source_name: 'Indeed',
        external_id: 'IND-002',
        title: 'Product Manager',
        company: 'ResuSwipe Innovation Labs',
        location: 'San Francisco, CA',
        remote_type: 'remote' as const,
        salary_min: 110000,
        salary_max: 150000,
        employment_type: 'full_time' as const,
        description: 'Drive product strategy and roadmap for our career development platform. Collaborate with engineering and design teams.',
        requirements: '3+ years product management experience. Strong analytical skills. Experience with Agile methodologies.',
        url: 'https://example.com/jobs/ind-002',
        skills_required: ['Product Management', 'Agile', 'User Research', 'Analytics', 'Roadmapping'],
        experience_level: 'mid' as const,
        industry: 'technology'
      },
      {
        source_name: 'LinkedIn Jobs',
        external_id: 'LI-001',
        title: 'Full Stack Developer',
        company: 'ResuSwipe Engineering',
        location: 'San Jose, CA',
        remote_type: 'hybrid' as const,
        salary_min: 110000,
        salary_max: 145000,
        employment_type: 'full_time' as const,
        description: 'Work across the entire stack building features for our career platform. Collaborate with cross-functional teams.',
        requirements: '4+ years full stack development. Proficiency in React and Node.js.',
        url: 'https://linkedin.com/jobs/li-001',
        skills_required: ['React', 'Node.js', 'TypeScript', 'PostgreSQL', 'GraphQL'],
        experience_level: 'senior' as const,
        industry: 'technology'
      },
      {
        source_name: 'Glassdoor',
        external_id: 'GD-001',
        title: 'Software Engineering Manager',
        company: 'ResuSwipe Leadership',
        location: 'San Francisco, CA',
        remote_type: 'hybrid' as const,
        salary_min: 150000,
        salary_max: 200000,
        employment_type: 'full_time' as const,
        description: 'Lead a team of talented engineers building innovative career solutions. Drive technical excellence and team growth.',
        requirements: '7+ years software engineering experience with 2+ years in management. Strong leadership skills.',
        url: 'https://glassdoor.com/jobs/gd-001',
        skills_required: ['Leadership', 'Software Engineering', 'Team Management', 'Architecture', 'Mentoring'],
        experience_level: 'senior' as const,
        industry: 'technology'
      },
      {
        source_name: 'ZipRecruiter',
        external_id: 'ZR-001',
        title: 'Senior Backend Engineer',
        company: 'ResuSwipe Backend Services',
        location: 'Remote',
        remote_type: 'remote' as const,
        salary_min: 125000,
        salary_max: 165000,
        employment_type: 'full_time' as const,
        description: 'Build robust, scalable backend systems. Design APIs and optimize database performance.',
        requirements: '5+ years backend engineering experience. Expert knowledge of Node.js or Python.',
        url: 'https://ziprecruiter.com/jobs/zr-001',
        skills_required: ['Node.js', 'Python', 'PostgreSQL', 'Redis', 'Microservices'],
        experience_level: 'senior' as const,
        industry: 'technology'
      },
      {
        source_name: 'Monster',
        external_id: 'MON-001',
        title: 'Staff Software Engineer',
        company: 'ResuSwipe Advanced Engineering',
        location: 'San Francisco, CA',
        remote_type: 'hybrid' as const,
        salary_min: 160000,
        salary_max: 220000,
        employment_type: 'full_time' as const,
        description: 'Lead technical architecture and mentor senior engineers. Drive technical excellence across the organization.',
        requirements: '8+ years software engineering experience. Deep expertise in distributed systems.',
        url: 'https://monster.com/jobs/mon-001',
        skills_required: ['Architecture', 'Distributed Systems', 'Mentoring', 'System Design', 'Leadership'],
        experience_level: 'senior' as const,
        industry: 'technology'
      }
    ];

    let successCount = 0;
    let errorCount = 0;

    for (const job of testJobs) {
      const sourceId = sourceMap.get(job.source_name);

      if (!sourceId) {
        console.warn(`Source not found: ${job.source_name}`);
        errorCount++;
        continue;
      }

      const { error: insertError } = await supabase
        .from('scraped_jobs')
        .insert({
          source_id: sourceId,
          external_id: job.external_id,
          title: job.title,
          company: job.company,
          location: job.location,
          remote_type: job.remote_type,
          salary_min: job.salary_min,
          salary_max: job.salary_max,
          employment_type: job.employment_type,
          description: job.description,
          requirements: job.requirements,
          url: job.url,
          posted_date: new Date().toISOString(),
          skills_required: job.skills_required,
          experience_level: job.experience_level,
          industry: job.industry,
          is_active: true
        });

      if (insertError) {
        if (insertError.code === '23505') {
          console.log(`Job ${job.external_id} already exists, skipping...`);
        } else {
          console.error(`Error inserting job ${job.external_id}:`, insertError);
          errorCount++;
        }
      } else {
        successCount++;
        console.log(`✓ Inserted job: ${job.title} from ${job.source_name}`);
      }
    }

    console.log(`\nSeeding complete: ${successCount} jobs inserted, ${errorCount} errors`);
    return { success: true, inserted: successCount, errors: errorCount };
  } catch (error) {
    console.error('Error in seedJobsNow:', error);
    return { success: false, error };
  }
}
